import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Label } from './ui/label';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  PlusCircle, 
  Clock, 
  CheckCircle, 
  BarChart3, 
  Users, 
  Settings,
  LogOut,
  Globe,
  Copy,
  AlertTriangle,
  Lock,
  ClipboardList,
  KeyRound,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import NotificationBell from './NotificationBell';
import NOM035Notifications from './NOM035Notifications';
import EnneagramDashboard from './EnneagramDashboard';
import EmployeeActionPlanView from './EmployeeActionPlanView';
import NOM035EmployeePanel from './NOM035EmployeePanel';
import axios from 'axios';
import logger from '../utils/logger';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, changePassword } = useAuth();
  const { language, changeLanguage, t } = useLanguage();
  const { canCreateEvaluation, getRemainingEvaluations, hasAccess, hasActiveSubscription } = useSubscription();
  const [assessments, setAssessments] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [targetEmployees, setTargetEmployees] = useState(0);
  
  // Password change dialog state
  const [showPasswordChangeDialog, setShowPasswordChangeDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Evaluation types config
  const [cultureEnabled, setCultureEnabled] = useState(true);
  const [personalityEnabled, setPersonalityEnabled] = useState(true);

  // Check if user needs to change password
  useEffect(() => {
    if (user?.force_password_change) {
      setShowPasswordChangeDialog(true);
    }
  }, [user?.force_password_change]);

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error(
        language === 'es' 
          ? 'La contraseña debe tener al menos 6 caracteres' 
          : 'Password must be at least 6 characters'
      );
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error(
        language === 'es' 
          ? 'Las contraseñas no coinciden' 
          : 'Passwords do not match'
      );
      return;
    }
    
    setChangingPassword(true);
    try {
      const result = await changePassword(newPassword);
      if (result.success) {
        toast.success(
          language === 'es' 
            ? 'Contraseña actualizada correctamente' 
            : 'Password updated successfully'
        );
        setShowPasswordChangeDialog(false);
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast.error(result.error || (language === 'es' ? 'Error al cambiar la contraseña' : 'Error changing password'));
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error al cambiar la contraseña' : 'Error changing password');
    } finally {
      setChangingPassword(false);
    }
  };

  useEffect(() => {
    loadAssessments();
    loadEvaluationConfig();
    if (user?.rol === 'company_admin') {
      loadCompanyInfo();
      loadCandidates();
    }
  }, [user]);

  const loadEvaluationConfig = async () => {
    try {
      if (user?.empresa_id) {
        const response = await axios.get(`/v2/assessment-config/resolve?empresa_id=${user.empresa_id}`);
        if (response.data.success) {
          setCultureEnabled(response.data.data.culture_enabled !== false);
          setPersonalityEnabled(response.data.data.personality_enabled !== false);
        }
      }
    } catch (error) {
      logger.error('Error loading evaluation config:', error);
      // Default to both enabled if error
      setCultureEnabled(true);
      setPersonalityEnabled(true);
    }
  };

  const loadAssessments = async () => {
    try {
      const response = await axios.get('/assessments');
      if (response.data.success) {
        // For company_admin, only show company assessments (company_complete)
        // For employees/candidates, show all their assessments
        const allAssessments = response.data.data;
        const filteredAssessments = user?.rol === 'company_admin' 
          ? allAssessments.filter(a => a.tipo === 'company_complete')
          : allAssessments;
        setAssessments(filteredAssessments);
      }
    } catch (error) {
      logger.error('Error loading assessments:', error);
      toast.error(t('dashboard.error_loading_assessments', 'Error loading assessments'));
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyInfo = async () => {
    try {
      const response = await axios.get('/companies/my-company');
      if (response.data.success) {
        setCompanyInfo(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading company info:', error);
    }
  };

  const copyCompanyCode = () => {
    if (companyInfo?.codigo_acceso) {
      navigator.clipboard.writeText(companyInfo.codigo_acceso);
      toast.success(t('dashboard.code_copied', 'Code copied to clipboard'));
    }
  };

  const updateEmployeeTarget = async () => {
    try {
      const response = await axios.put('/companies/employee-target', {
        target_employees: targetEmployees
      });
      
      if (response.data.success) {
        toast.success(t('dashboard.target_updated', 'Target updated successfully'));
        setShowTargetModal(false);
        loadCompanyInfo(); // Reload to get updated info
      }
    } catch (error) {
      toast.error(t('dashboard.error_updating_target', 'Error updating target'));
    }
  };

  const loadCandidates = async () => {
    try {
      const response = await axios.get('/companies/employees-and-candidates');
      if (response.data.success) {
        const candidatesList = response.data.data.filter(user => user.rol === 'candidate');
        setCandidates(candidatesList);
      }
    } catch (error) {
      logger.error('Error loading candidates:', error);
    }
  };

  const getEmployeeStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'not_started': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const createAssessment = async (tipo, idioma = language) => {
    // Check subscription limits before creating
    if (!canCreateEvaluation()) {
      const remaining = getRemainingEvaluations();
      if (remaining === 0) {
        toast.error(
          language === 'es' 
            ? 'Has alcanzado el límite de evaluaciones de tu plan. Contacta al administrador para actualizar.' 
            : 'You have reached your plan\'s evaluation limit. Contact administrator to upgrade.'
        );
        return;
      }
    }
    
    try {
      setLoading(true);
      const response = await axios.post('/assessments', {
        tipo,
        idioma
      });

      if (response.data.success) {
        const assessmentId = response.data.data.id;
        toast.success(t('assessment_created', 'Evaluación creada exitosamente'));
        navigate(`/assessment/${assessmentId}`);
      } else {
        toast.error(response.data.message || t('error_creating_assessment', 'Error al crear evaluación'));
      }
    } catch (error) {
      logger.error('Error creating assessment:', error);
      toast.error(error.response?.data?.detail || t('error_creating_assessment', 'Error al crear evaluación'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'iniciado': { color: 'bg-blue-100 text-blue-800', text: t('status_started', 'Iniciado') },
      'en_progreso': { color: 'bg-yellow-100 text-yellow-800', text: t('status_in_progress', 'En Progreso') },
      'completado': { color: 'bg-green-100 text-green-800', text: t('status_completed', 'Completado') },
      'abandonado': { color: 'bg-red-100 text-red-800', text: t('status_abandoned', 'Abandonado') },
      'validado': { color: 'bg-purple-100 text-purple-800', text: t('status_validated', 'Validado') }
    };

    const statusInfo = statusMap[status] || statusMap['iniciado'];
    return (
      <Badge className={statusInfo.color}>
        {statusInfo.text}
      </Badge>
    );
  };

  const getAssessmentTypeLabel = (tipo) => {
    const typeMap = {
      'individual_complete': t('assessment_types.individual_complete', 'Evaluación Individual Completa'),
      'individual_express': t('assessment_types.individual_express', 'Evaluación Individual Express'),
      'candidate_complete': t('assessment_types.candidate_complete', 'Evaluación de Candidato Completa'),
      'candidate_express': t('assessment_types.candidate_express', 'Evaluación de Candidato Express'),
      'company_complete': t('assessment_types.company_complete', 'Evaluación Empresarial Completa'),
      'company_express': t('assessment_types.company_express', 'Evaluación Empresarial Express')
    };

    return typeMap[tipo] || tipo;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US');
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${minutes}m`;
  };

  if (loading && assessments.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#1A878D' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b" style={{ borderBottomColor: '#1A878D', borderBottomWidth: '2px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img 
                src="/images/mevia-logo-horizontal.png" 
                alt="MEVIA" 
                className="h-10"
              />
              <div className="border-l pl-4" style={{ borderColor: '#E5E7EB' }}>
                <h1 className="text-xl font-bold text-gray-900">
                  {t('welcome', 'Bienvenido')}, {user?.nombre}
                </h1>
                <p className="text-sm text-gray-600">
                  {user?.empresa_nombre || t('individual_user', 'Usuario Individual')}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* NOM-035 Notifications */}
              <NOM035Notifications onNavigate={(url) => navigate(url)} />
              
              {/* General Notifications */}
              <NotificationBell />
              
              {/* Language selector */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}
                data-testid="language-toggle"
              >
                <Globe className="h-4 w-4 mr-2" />
                {language === 'es' ? 'EN' : 'ES'}
              </Button>
              
              {/* Settings */}
              <Button variant="ghost" size="sm">
                <Settings className="h-4 w-4" />
              </Button>
              
              {/* Logout */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={logout}
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Company Info Card - Only for company admins */}
        {user?.rol === 'company_admin' && companyInfo && (
          <Card className="mb-8 bg-gradient-to-r from-gray-50 to-white border-l-4" style={{ borderLeftColor: '#1A878D' }}>
            <CardHeader>
              <CardTitle className="text-xl" style={{ color: '#0B6A7D' }}>
                {t('dashboard.company_control_panel', 'Panel de Control Empresarial')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                {/* Company Code Section */}
                <div className="md:col-span-1">
                  <div className="bg-white p-4 rounded-lg border-2 border-blue-300">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {t('dashboard.company_code', 'Código de Empresa')}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-mono font-bold text-blue-600 bg-blue-50 px-3 py-2 rounded">
                        {companyInfo.codigo_acceso}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyCompanyCode}
                        className="ml-2"
                        data-testid="copy-company-code"
                      >
                        📋 {t('buttons.copy', 'Copiar')}
                      </Button>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      {t('dashboard.company_code_help', 'Comparte este código con tus empleados para que puedan registrarse')}
                    </p>
                  </div>

                  {/* Employee Target Setting */}
                  <div className="bg-white p-4 rounded-lg border mt-4">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {t('dashboard.assessment_target', 'Objetivo de Evaluación')}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-purple-600">
                        {companyInfo.employees_completed || 0}/{(companyInfo.configuracion_assessments?.target_employees) || companyInfo.total_employees || 0}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setTargetEmployees((companyInfo.configuracion_assessments?.target_employees) || companyInfo.total_employees || 0);
                          setShowTargetModal(true);
                        }}
                      >
                        ⚙️ {t('buttons.adjust', 'Ajustar')}
                      </Button>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      {t('dashboard.employees_completed_help', 'Empleados que han completado evaluaciones')}
                    </p>
                  </div>
                </div>

                {/* Enhanced Company Stats */}
                <div className="md:col-span-2">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white p-4 rounded-lg">
                      <p className="text-sm text-gray-600">
                        {language === 'es' ? 'Sin Empezar' : 'Not Started'}
                      </p>
                      <p className="text-2xl font-bold text-gray-600">
                        {companyInfo.employees_not_started || 0}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t('dashboard.employees', 'employees')}
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <p className="text-sm text-gray-600">
                        {t('in_progress', 'En Progreso')}
                      </p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {companyInfo.employees_in_progress || 0}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t('dashboard.employees', 'employees')}
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <p className="text-sm text-gray-600">
                        {language === 'es' ? 'Completadas' : 'Completed'}
                      </p>
                      <p className="text-2xl font-bold text-green-600">
                        {companyInfo.employees_completed || 0}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t('dashboard.employees', 'employees')}
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <p className="text-sm text-gray-600">
                        {t('active_employees', 'Empleados Activos')}
                      </p>
                      <p className="text-2xl font-bold text-blue-600">
                        {companyInfo.active_employees}/{companyInfo.total_employees}
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <p className="text-sm text-gray-600">
                        {t('dashboard.completion_rate', 'Completion Rate')}
                      </p>
                      <p className="text-2xl font-bold text-purple-600">
                        {companyInfo.completion_rate}%
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <p className="text-sm text-gray-600">
                        {language === 'es' ? 'Industria' : 'Industry'}
                      </p>
                      <p className="text-lg font-medium text-gray-900">
                        {companyInfo.industria || (language === 'es' ? 'No especificada' : 'Not specified')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed Employee List */}
              {companyInfo.employee_details && companyInfo.employee_details.length > 0 && (
                <div className="mt-6">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-gray-900">
                      {t('dashboard.employee_status', 'Employee Status')}
                    </h4>
                    <div className="flex space-x-2 flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => navigate('/reports')}>
                        📊 {t('buttons.view_reports', 'Ver Reportes')}
                      </Button>
                      {(user?.rol === 'super_admin' || user?.rol === 'company_admin') && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => navigate('/assessment-config')}
                            className="border-purple-500 text-purple-700 hover:bg-purple-50"
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            {language === 'es' ? 'Configuración V2' : 'V2 Config'}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => navigate('/assessments-management')}>
                            📝 {language === 'es' ? 'Gestionar Evaluaciones' : 'Manage Assessments'}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => navigate('/admin')}>
                            🛡️ {t('buttons.admin_panel', 'Admin Panel')}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg border">
                    <div className="max-h-64 overflow-y-auto">
                      {companyInfo.employee_details.map((employee, index) => (
                        <div key={index} className={`p-3 ${index !== companyInfo.employee_details.length - 1 ? 'border-b' : ''}`}>
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium">{employee.nombre}</span>
                                <span className="text-sm text-gray-600">({employee.email})</span>
                                {employee.departamento && (
                                  <span className="text-sm text-gray-500">- {employee.departamento}</span>
                                )}
                              </div>
                              <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                                <span>
                                  {language === 'es' ? 'Evaluaciones' : 'Assessments'}: {employee.completed_assessments}/{employee.total_assessments}
                                </span>
                                {employee.last_completed && (
                                  <span>
                                    {language === 'es' ? 'Último completado' : 'Last completed'}: {formatDate(employee.last_completed)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Badge variant={employee.activo ? "success" : "destructive"}>
                                {employee.activo 
                                  ? (language === 'es' ? 'Activo' : 'Active')
                                  : (language === 'es' ? 'Inactivo' : 'Inactive')
                                }
                              </Badge>
                              <Badge className={getEmployeeStatusColor(employee.assessment_status)}>
                                {employee.assessment_status === 'completed' && t('status_completed', 'Completed')}
                                {employee.assessment_status === 'pending' && t('in_progress', 'En Progreso')}
                                {employee.assessment_status === 'not_started' && t('not_started', 'Sin Empezar')}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Candidates Section */}
        {user?.rol === 'company_admin' && candidates.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="h-5 w-5 mr-2" />
                {t('candidates', 'Candidatos')}
              </CardTitle>
              <CardDescription>
                {t('candidates_desc', 'Candidatos registrados para evaluación')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Total Candidates */}
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {candidates.length}
                  </div>
                  <div className="text-sm text-gray-600">
                    {t('total_candidates', 'Total Candidatos')}
                  </div>
                </div>
                
                {/* Completed Assessments */}
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {candidates.filter(c => c.completed_assessments > 0).length}
                  </div>
                  <div className="text-sm text-gray-600">
                    {language === 'es' ? 'Evaluados' : 'Assessed'}
                  </div>
                </div>
                
                {/* Pending Assessments */}
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {candidates.filter(c => c.completed_assessments === 0).length}
                  </div>
                  <div className="text-sm text-gray-600">
                    {language === 'es' ? 'Pendientes' : 'Pending'}
                  </div>
                </div>
              </div>

              {/* Candidates List */}
              <div className="mt-6">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-900">
                    {t('dashboard.candidate_status', 'Candidate Status')}
                  </h4>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={() => navigate('/reports')}>
                      👥 {t('buttons.manage_candidates', 'Ver Candidatos')}
                    </Button>
                    {(user?.rol === 'super_admin' || user?.rol === 'company_admin') && (
                      <Button variant="outline" size="sm" onClick={() => navigate('/admin')}>
                        🛡️ {t('buttons.admin_panel', 'Admin Panel')}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-lg border">
                  <div className="max-h-64 overflow-y-auto">
                    {candidates.map((candidate, index) => (
                      <div key={index} className={`p-3 ${index !== candidates.length - 1 ? 'border-b' : ''}`}>
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium">{candidate.nombre}</span>
                              <span className="text-sm text-gray-600">({candidate.email})</span>
                            </div>
                            <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                              <span>
                                {language === 'es' ? 'Evaluaciones' : 'Assessments'}: {candidate.completed_assessments}
                              </span>
                              {candidate.created_at && (
                                <span>
                                  {language === 'es' ? 'Registrado' : 'Registered'}: {formatDate(candidate.created_at)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Badge className={candidate.completed_assessments > 0 ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                              {candidate.completed_assessments > 0 
                                ? (language === 'es' ? 'Evaluado' : 'Assessed')
                                : (language === 'es' ? 'Pendiente' : 'Pending')
                              }
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Enneagram Dashboard - Only for company admins when personality is enabled */}
        {user?.rol === 'company_admin' && personalityEnabled && (
          <div className="mb-8">
            <EnneagramDashboard />
          </div>
        )}

        {/* Target Setting Modal */}
        {showTargetModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-96">
              <CardHeader>
                <CardTitle>
                  {language === 'es' ? 'Establecer Objetivo' : 'Set Target'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="target-employees">
                    {t('dashboard.target_employees_desc', 'Target number of employees to assess')}
                  </Label>
                  <input
                    id="target-employees"
                    type="number"
                    min="1"
                    max={companyInfo?.total_employees || 100}
                    value={targetEmployees}
                    onChange={(e) => setTargetEmployees(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border rounded-md mt-2"
                  />
                  <p className="text-sm text-gray-600 mt-1">
                    {language === 'es' 
                      ? t('dashboard.total_employees_registered', 'Total employees registered: ') + (companyInfo?.total_employees || 0)
                      : `Total registered employees: ${companyInfo?.total_employees || 0}`
                    }
                  </p>
                </div>
                <div className="flex space-x-2">
                  <Button variant="outline" onClick={() => setShowTargetModal(false)}>
                    {language === 'es' ? 'Cancelar' : 'Cancel'}
                  </Button>
                  <Button onClick={updateEmployeeTarget}>
                    {language === 'es' ? 'Guardar' : 'Save'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* NOM-035 Panel for Employees */}
        {user?.rol === 'employee' && user?.empresa_id && (
          <NOM035EmployeePanel user={user} />
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('total_assessments', 'Total Evaluaciones')}</p>
                  <p className="text-2xl font-bold text-gray-900">{assessments.length}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('completed', 'Completadas')}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {assessments.filter(a => a.status === 'completado').length}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('in_progress', 'En Progreso')}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {assessments.filter(a => a.status === 'en_progreso' || a.status === 'iniciado').length}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('avg_progress', 'Progreso Promedio')}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {assessments.length > 0 
                      ? Math.round(assessments.reduce((acc, a) => acc + a.progreso_porcentaje, 0) / assessments.length) 
                      : 0}%
                  </p>
                </div>
                <Users className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Individual Culture assessment - hidden for company admins and when culture is disabled */}
          {user?.rol !== 'company_admin' && cultureEnabled && (
            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => createAssessment('individual_complete')}>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <PlusCircle className="h-5 w-5 mr-2 text-blue-600" />
                  {t('assessment_types.individual_complete', 'Evaluación Completa')}
                </CardTitle>
                <CardDescription>
                  {t('assessment_complete_desc', 'Evaluación detallada de 45-50 minutos')}
                </CardDescription>
              </CardHeader>
            </Card>
          )}
          
          {/* Enneagram Assessment Card - Available for employees and candidates when personality is enabled */}
          {user?.rol !== 'company_admin' && personalityEnabled && (
            <Card 
              className="cursor-pointer hover:shadow-lg transition-shadow border-l-4" 
              style={{ borderLeftColor: '#1A878D' }}
              onClick={() => navigate('/enneagram')}
            >
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <span className="text-xl mr-2">🎭</span>
                  {language === 'es' ? 'Evaluación Eneagrama' : 'Enneagram Assessment'}
                </CardTitle>
                <CardDescription>
                  {language === 'es' 
                    ? 'Descubre tu tipo de personalidad (15-20 min)' 
                    : 'Discover your personality type (15-20 min)'}
                </CardDescription>
              </CardHeader>
            </Card>
          )}
          
          {/* Express assessment temporarily hidden 
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => createAssessment('individual_express')}>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <PlusCircle className="h-5 w-5 mr-2 text-green-600" />
                {t('assessment_types.individual_express', 'Evaluación Express')}
              </CardTitle>
              <CardDescription>
                {t('assessment_express_desc', 'Evaluación rápida de 15-20 minutos')}
              </CardDescription>
            </CardHeader>
          </Card>
          */}
          
          {user?.rol === 'company_admin' && (
            <>
              {cultureEnabled && (
                <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => createAssessment('company_complete')}>
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <PlusCircle className="h-5 w-5 mr-2 text-purple-600" />
                      {t('assessment_types.company_complete', 'Evaluación Empresarial')}
                    </CardTitle>
                    <CardDescription>
                      {t('company_assessment_desc', 'Evaluación organizacional completa')}
                    </CardDescription>
                  </CardHeader>
                </Card>
              )}
              
              {/* Company express assessment temporarily hidden
              <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => createAssessment('company_express')}>
                <CardHeader>
                  <CardTitle className="flex items-center text-lg">
                    <PlusCircle className="h-5 w-5 mr-2 text-orange-600" />
                    {t('assessment_types.company_express', 'Evaluación Empresarial Express')}
                  </CardTitle>
                  <CardDescription>
                    {t('company_express_desc', 'Evaluación organizacional rápida')}
                  </CardDescription>
                </CardHeader>
              </Card>
              */}
            </>
          )}
          
          {/* Show message if no evaluations are enabled */}
          {user?.rol !== 'company_admin' && !cultureEnabled && !personalityEnabled && (
            <Card className="col-span-full border-dashed border-2 border-gray-300">
              <CardContent className="py-8 text-center">
                <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  {language === 'es' 
                    ? 'No hay evaluaciones habilitadas actualmente. Contacta a tu administrador.'
                    : 'No assessments are currently enabled. Contact your administrator.'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Assessments List */}
        <Card>
          <CardHeader>
            <CardTitle>{t('my_assessments', 'Mis Evaluaciones')}</CardTitle>
            <CardDescription>
              {t('assessments_desc', 'Historial de todas tus evaluaciones')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {assessments.length === 0 ? (
              <div className="text-center py-8">
                <BarChart3 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">
                  {t('no_assessments', 'No tienes evaluaciones aún')}
                </p>
                <p className="text-sm text-gray-400">
                  {t('create_first_assessment', 'Crea tu primera evaluación para comenzar')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {assessments.map((assessment) => (
                  <div 
                    key={assessment.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/assessment/${assessment.id}`)}
                    data-testid={`assessment-item-${assessment.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-medium text-gray-900">
                            {getAssessmentTypeLabel(assessment.tipo)}
                          </h3>
                          {getStatusBadge(assessment.status)}
                          <span className="text-sm text-gray-500">
                            {assessment.idioma.toUpperCase()}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span>
                            {t('created', 'Created')}: {formatDate(assessment.fecha_inicio)}
                          </span>
                          {assessment.fecha_completado && (
                            <span>
                              {t('completed', 'Completed')}: {formatDate(assessment.fecha_completado)}
                            </span>
                          )}
                          <span>
                            {t('duration', 'Duration')}: {formatDuration(assessment.tiempo_total_segundos)}
                          </span>
                        </div>
                        
                        <div className="mt-2">
                          <div className="flex items-center space-x-2">
                            <Progress value={assessment.progreso_porcentaje} className="flex-1" />
                            <span className="text-sm font-medium text-gray-900">
                              {Math.round(assessment.progreso_porcentaje)}%
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="ml-4">
                        <Button 
                          variant="outline" 
                          size="sm"
                          data-testid={`continue-assessment-${assessment.id}`}
                        >
                          {assessment.status === 'completado' 
                            ? t('view_results', 'View Results')
                            : t('continue', 'Continue')
                          }
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Plan Section - Only for employees */}
        {user?.rol === 'employee' && (
          <div className="mt-8">
            <EmployeeActionPlanView />
          </div>
        )}
      </main>

      {/* Force Password Change Dialog */}
      <Dialog open={showPasswordChangeDialog} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-teal-600" />
              {language === 'es' ? 'Cambiar Contraseña' : 'Change Password'}
            </DialogTitle>
            <DialogDescription>
              {language === 'es' 
                ? 'Por seguridad, debes establecer una nueva contraseña antes de continuar.' 
                : 'For security, you must set a new password before continuing.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">
                {language === 'es' ? 'Nueva contraseña' : 'New password'}
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={language === 'es' ? 'Mínimo 6 caracteres' : 'Minimum 6 characters'}
                  className="pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                {language === 'es' ? 'Confirmar contraseña' : 'Confirm password'}
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={language === 'es' ? 'Repite la contraseña' : 'Repeat password'}
                  className="pr-10"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button 
              onClick={handlePasswordChange} 
              disabled={changingPassword || !newPassword || !confirmPassword}
              data-testid="change-password-btn"
            >
              {changingPassword 
                ? (language === 'es' ? 'Guardando...' : 'Saving...') 
                : (language === 'es' ? 'Cambiar contraseña' : 'Change password')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;